import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { readAttachmentContent } from '@evogard/ui';
import type { ExpenseFinanceActBody } from '@evogard/contract';
import { loadFailureMessage } from '@evogard/domain';
import { api, ok } from '../../../lib/api.js';
import { useSessionContext } from '../../../lib/session.js';

/** A raiz de tudo do Financeiro: o quadro, a despesa aberta e a mesa dela. */
export const FINANCE_EXPENSES_KEY = ['finance', 'expenses'] as const;

function useTenant() {
  return useSessionContext().data?.currentTenant.id;
}

/** A despesa aberta, com o caminho e o estado do Conta Azul. */
export function useFinanceExpense(id: number) {
  const tenant = useTenant();
  return useQuery({
    queryKey: [...FINANCE_EXPENSES_KEY, tenant, 'detalhe', id],
    queryFn: async () =>
      ok(
        await api.financeExpenses.getFinanceExpense({ params: { id } }),
        200,
        loadFailureMessage(undefined, 'a despesa'),
      ),
  });
}

export function useFinanceActivity(id: number) {
  const tenant = useTenant();
  return useQuery({
    queryKey: [...FINANCE_EXPENSES_KEY, tenant, 'atividade', id],
    queryFn: async () =>
      ok(
        await api.financeExpenses.financeExpenseActivity({ params: { id } }),
        200,
        loadFailureMessage(undefined, 'a atividade'),
      ),
  });
}

export function useFinanceFiles(id: number) {
  const tenant = useTenant();
  return useQuery({
    queryKey: [...FINANCE_EXPENSES_KEY, tenant, 'anexos', id],
    queryFn: async () =>
      ok(
        await api.financeExpenses.listFinanceExpenseFiles({ params: { id } }),
        200,
        loadFailureMessage(undefined, 'os anexos'),
      ),
  });
}

export function useFinanceLabelOptions(enabled: boolean) {
  const tenant = useTenant();
  return useQuery({
    queryKey: [...FINANCE_EXPENSES_KEY, tenant, 'etiquetas'],
    enabled,
    queryFn: async () =>
      ok(
        await api.financeExpenses.financeLabelOptions(),
        200,
        loadFailureMessage(undefined, 'as etiquetas'),
      ),
  });
}

export function useFinanceAssigneeOptions(enabled: boolean) {
  const tenant = useTenant();
  return useQuery({
    queryKey: [...FINANCE_EXPENSES_KEY, tenant, 'responsaveis'],
    enabled,
    queryFn: async () =>
      ok(
        await api.financeExpenses.financeAssigneeOptions(),
        200,
        loadFailureMessage(undefined, 'quem pode ser o responsável'),
      ),
  });
}

/** Tudo que grava na mesa — e recarrega o quadro e a despesa aberta. */
export function useFinanceDeskMutations(id: number) {
  const client = useQueryClient();
  const onSettled = () => client.invalidateQueries({ queryKey: FINANCE_EXPENSES_KEY });
  return {
    act: useMutation({
      mutationFn: async (body: ExpenseFinanceActBody) =>
        ok(
          await api.financeExpenses.financeAct({ params: { id }, body }),
          200,
          'Não foi possível concluir.',
        ),
      onSettled,
    }),
    setLabels: useMutation({
      mutationFn: async (labelIds: number[]) =>
        ok(
          await api.financeExpenses.setFinanceExpenseLabels({
            params: { id },
            body: { labelIds },
          }),
          200,
          'Não foi possível gravar as etiquetas.',
        ),
      onSettled,
    }),
    setAssignee: useMutation({
      mutationFn: async (userId: string | null) =>
        ok(
          await api.financeExpenses.setFinanceExpenseAssignee({
            params: { id },
            body: { userId },
          }),
          200,
          'Não foi possível trocar o responsável.',
        ),
      onSettled,
    }),
    comment: useMutation({
      mutationFn: async (body: string) =>
        ok(
          await api.financeExpenses.commentFinanceExpense({
            params: { id },
            body: { body },
          }),
          201,
          'Não foi possível comentar.',
        ),
      onSettled,
    }),
    updateComment: useMutation({
      mutationFn: async ({
        commentId,
        change,
      }: {
        commentId: number;
        change: { body: string } | { archived: true };
      }) =>
        ok(
          await api.financeExpenses.updateFinanceExpenseComment({
            params: { id, commentId },
            body: change,
          }),
          200,
          'Não foi possível mudar o comentário.',
        ),
      onSettled,
    }),
    attach: useMutation({
      mutationFn: async ({
        file,
        kind,
      }: {
        file: File;
        kind: 'nota_fiscal' | 'comprovante' | 'outro';
      }) =>
        ok(
          await api.financeExpenses.attachFinanceExpenseFile({
            params: { id },
            body: {
              kind,
              fileName: file.name,
              mimeType: file.type || 'application/octet-stream',
              content: await readAttachmentContent(file),
            },
          }),
          201,
          `Não foi possível enviar ${file.name}.`,
        ),
      onSettled,
    }),
    archiveFile: useMutation({
      mutationFn: async (fileId: number) =>
        ok(
          await api.financeExpenses.archiveFinanceExpenseFile({
            params: { id, fileId },
            body: {},
          }),
          200,
          'Não foi possível arquivar o anexo.',
        ),
      onSettled,
    }),
  };
}

/** A URL assinada de curta duração de um arquivo da despesa — a prévia dos anexos. */
export async function financeFileUrl(id: number, key: string): Promise<string> {
  return ok(
    await api.financeExpenses.financeExpenseFileUrl({ params: { id, key } }),
    200,
    'Não foi possível abrir o arquivo.',
  ).url;
}

/** Abre um arquivo da despesa numa aba nova, pela URL assinada de curta duração. */
export async function openFinanceFile(id: number, key: string): Promise<void> {
  const tab = window.open('', '_blank');
  const body = ok(
    await api.financeExpenses.financeExpenseFileUrl({ params: { id, key } }),
    200,
    'Não foi possível abrir o arquivo.',
  );
  if (tab) tab.location.href = body.url;
  else window.location.href = body.url;
}

/**
 * O beneficiário da despesa — a seção Dados bancários do Formulário e a
 * gaveta leem a mesma consulta, e uma não pede de novo o que a outra trouxe.
 * Mora fora de `FINANCE_EXPENSES_KEY` e não refaz ao voltar o foco: para quem
 * vê dados bancários, cada pedido é uma linha na auditoria.
 */
export function useFinanceBeneficiary(id: number) {
  const tenant = useTenant();
  return useQuery({
    queryKey: ['finance', 'expense-beneficiary', tenant, id],
    queryFn: async () =>
      ok(
        await api.financeExpenses.financeExpenseBeneficiary({ params: { id } }),
        200,
        loadFailureMessage(undefined, 'o beneficiário'),
      ),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

/** O sinistro que pediu a despesa, para a gaveta — pedido só quando ela abre. */
export function useFinanceClaim(id: number) {
  const tenant = useTenant();
  return useQuery({
    queryKey: [...FINANCE_EXPENSES_KEY, tenant, 'sinistro', id],
    queryFn: async () =>
      ok(
        await api.financeExpenses.financeExpenseClaim({ params: { id } }),
        200,
        loadFailureMessage(undefined, 'o sinistro'),
      ),
  });
}
